/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import { Modal, Button } from "react-bootstrap";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../auth";
import clsx from "clsx";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { ingredientSchema } from "../core/Schema";
import { Category, initialValues } from "../core/_models";
import { checkDuplicateCategory, updateCategory, viewCategoryById } from "../core/_requests";

interface ModalProps {
  show: boolean;
  onHide: () => void;
  id: string; // Receive ID of the ingredient
  onUpdate?: () => void; // Callback to refresh list after update
}

const EditCategory: React.FC<ModalProps> = ({ show, onHide, id, onUpdate }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<Category>(initialValues);
  const [duplicate, setDuplicate] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const inputs = useRef<Array<HTMLInputElement | HTMLSelectElement | null>>([]);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (id) {
      viewCategoryById(id).then((res) => {
        setData(res?.data); // Set initial data
      });
    }
  }, [id]);

  const formik = useFormik<Category>({
    initialValues: data,
    enableReinitialize: true,
    validationSchema: ingredientSchema,

    onSubmit: async (values, { resetForm }) => {
      values.chefId = currentUser?.uuid;
      values.createdBy = currentUser?.chefId;
      const updatedData = Object.assign(data, values);
            setLoading(true);
      updateCategory(id, updatedData)
        .then((res) => {
                    if (res?.status === true) {
            toast.success(res?.message);
            setData(initialValues);
            setLoading(false);
            if (onUpdate) onUpdate(); // Call parent callback to refresh list
            onHide();
          } else {
            toast.error(res?.message, { autoClose: 5000 });
            setLoading(false);
          }
          setData(initialValues);
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage("Something Went Wrong");
        });
      resetForm();
      setData(initialValues);
    },
  });

  return (
    <>
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            ref={formRef}
            onSubmit={formik.handleSubmit}
            noValidate
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
          >
            <div className="col-12 mb-3">
              <label htmlFor="" className="form-label require">
                Category
              </label>
              <input
                type="text"
                className={clsx(
                  "form-control",
                  {
                    "is-invalid": formik.touched.name && formik.errors.name,
                  },
                  {
                    "is-valid": formik.touched.name && !formik.errors.name,
                  }
                )}
                placeholder="Enter Your Category"
                ref={(el) => {
                  inputs.current[0] = el;
                }}
                {...formik.getFieldProps("name")}
                onChange={(e) => {
                  formik.handleChange(e);
                  // Perform duplicate check here
                  checkDuplicateCategory(e.target.value, id).then(
                    (response: any) => {
                      if (response?.isDuplicate === true) {
                        setDuplicate("Category name already exists");
                      } else {
                        setDuplicate("");
                      }
                    }
                  );
                }}
              />

              {formik.touched.name && formik.errors.name && (
                <div className="fv-plugins-message-container">
                  <span role="alert">{formik.errors.name}</span>
                </div>
              )}
              <span style={{ color: "red" }}>{duplicate ? duplicate : ""}</span>
            </div>
            <Modal.Footer className="d-flex justify-content-center">
              <Button
                className="btn btn-cancel rounded-5 small me-2 mt-1"
                onClick={onHide}
              >
                Close
              </Button>
              <button
                type="submit"
                disabled={formik.isSubmitting || !formik.isValid || !!duplicate}
                className="btn btn-outline-primary rounded-5 me-2"
              >
                {!loading && <span className="indicator-label">Publish</span>}
                {loading && (
                  <span
                    className="indicator-progress"
                    style={{ display: "block" }}
                  >
                    Please wait...
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                )}
              </button>
            </Modal.Footer>
          </form>
        </Modal.Body>
      </Modal>
      <ToastContainer /> {/* To render toast notifications */}
    </>
  );
};

export default EditCategory;
